import { FunctionalComponent, ComponentChildren } from 'preact'
import { AlertTriangle, Code2 } from 'lucide-preact'
import { useLocation } from 'preact-iso'
import { TrackingSnippet } from '@/components/tracking/TrackingSnippet'
import { useCompanyTracking } from '@/hooks/useCompanyTracking'
import { routes } from '@/utils/routes'

interface ExternalLandingPageTrackingProps {
  clientCompanyId: string
  workspaceId: string
  campaignId: string
  /** The landing page's Id. Embedded as `data-landing-page-id` so sessions from the
   *  customer's own page carry a LandingPageId and classify as Landing page (ORB-487). */
  landingPageId: string
}

function SettingsLink({ label, onGo }: { label: string; onGo: () => void }) {
  return (
    <button
      type="button"
      onClick={onGo}
      className="font-medium underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
    >
      {label}
    </button>
  )
}

function Notice({
  tone,
  icon: Icon,
  children,
}: {
  tone: string
  icon: typeof AlertTriangle
  children: ComponentChildren
}) {
  return (
    <p className={`flex items-start gap-1.5 text-xs ${tone}`}>
      <Icon className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />
      <span className="min-w-0">{children}</span>
    </p>
  )
}

const AMBER = 'text-amber-600 dark:text-amber-400'

/**
 * Recommended tracking snippet for a customer-hosted (External-mode) landing page (ORB-494).
 * The snippet embeds the landing page's Id, which the tracker sends as `scope.landingPageId` and
 * the API stamps onto the session — that non-null LandingPageId is what classifies the visit as a
 * Landing page session rather than a Website one on the contact timeline.
 *
 * Deliberately says nothing about approved domains (ORB-513). This page's host is auto-approved:
 * the API derives it from the landing page's own DeployedUrl, both for origin validation and for
 * the "Custom landing page" row on the Approved Domains screen, so a notice here could only ever
 * report success. It used to report failure instead — not by design, but because External-mode
 * pages were created without a ClientCompanyId and so were skipped by the tenant-scoped query
 * behind both of those (fixed in ORB-503). Domain state belongs on the settings screen that owns
 * it; a verification nudge lands here only if ORB-495's CNAME work introduces a state this page
 * can genuinely fail in.
 */
export const ExternalLandingPageTracking: FunctionalComponent<ExternalLandingPageTrackingProps> = ({
  clientCompanyId,
  workspaceId,
  campaignId,
  landingPageId,
}) => {
  const { tracking, isLoading, error } = useCompanyTracking(clientCompanyId)
  const { route } = useLocation()

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-6 space-y-4">
      <div className="flex items-start gap-2.5">
        <Code2 className="w-4 h-4 mt-0.5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Visitor tracking</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Paste this snippet into your page&apos;s <code className="font-mono">&lt;head&gt;</code> so
            visits are attributed to this landing page.
          </p>
        </div>
      </div>

      {isLoading && <div className="h-20 rounded bg-slate-200 dark:bg-slate-600 animate-pulse" />}

      {!isLoading && error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}

      {!isLoading && !error && tracking && (
        <>
          <TrackingSnippet
            TrackingId={tracking.TrackingId}
            ScriptUrl={tracking.ScriptUrl}
            WorkspaceId={workspaceId}
            CampaignId={campaignId}
            LandingPageId={landingPageId}
            label="Landing page tracking snippet"
          />
          {/* The company-level kill switch is the one thing this panel can still be wrong about —
              it's independent of the landing page and silences the snippet entirely. */}
          {!tracking.Enabled && (
            <Notice tone={AMBER} icon={AlertTriangle}>
              Visitor tracking is turned off for this company, so this snippet won&apos;t report.{' '}
              <SettingsLink label="Turn on visitor tracking" onGo={() => route(routes.settingsCompany(clientCompanyId))} />
            </Notice>
          )}
        </>
      )}
    </div>
  )
}
